import posthog from 'posthog-js'
import * as Sentry from '@sentry/react'

type EventName = 
  | 'page_view'
  | 'run_view'
  | 'alert_view'
  | 'alert_acknowledged'
  | 'ticket_created'
  | 'integration_connected'
  | 'integration_disconnected'
  | 'integration_clicked'
  | 'integration_setup_started'
  | 'integration_setup_step_completed'
  | 'integration_setup_failed'
  | 'integration_oauth_initiated'
  | 'integration_oauth_completed'
  | 'integration_oauth_failed'
  | 'github_integration_connected'
  | 'gitlab_integration_connected'
  | 'circleci_integration_connected'
  | 'jenkins_integration_connected'
  | 'slack_integration_connected'
  | 'teams_integration_connected'
  | 'api_key_created'
  | 'api_key_deleted'
  | 'api_key_env_copied'
  | 'api_key_cli_copied'
  | 'api_key_copy_attempted'
  | 'webhook_created'
  | 'webhook_deleted'
  | 'settings_tab_changed'
  | 'workspace_updated'
  | 'environment_created'
  | 'environment_updated'
  | 'environment_deleted'
  | 'environment_default_changed'
  | 'user_invited'
  | 'user_removed'
  | 'user_role_updated'
  | 'security_settings_updated'
  | 'audit_log_exported'
  | 'sso_toggled'
  | 'ip_allowlist_toggled'
  | 'data_retention_changed'
  | 'profile_updated'
  | 'team_member_viewed'
  | 'onboarding_completed'
  | 'dashboard_viewed'
  | 'setup_integration_clicked'
  | 'integration_modal_opened'
  | 'integration_completed'
  | 'run_tests_clicked'
  | 'chart_date_filter_changed'
  | 'failure_trend_zoom_clicked'
  | 'failure_trend_compare_clicked'
  | 'search_used'
  | 'notification_bell_clicked'
  | 'theme_toggled'
  | 'profile_menu_opened'

export type EventProperties = Record<string, string | number | boolean | null>

/**
 * Track a page view event
 */
export const trackPageView = (pageName: string, properties?: EventProperties) => {
  posthog.capture('page_view', {
    page: pageName,
    ...properties
  })
  
  // Track page view in Sentry
  Sentry.addBreadcrumb({
    category: 'navigation',
    message: `Page View: ${pageName}`,
    level: 'info',
    data: properties
  })
}

/**
 * Track a user action event
 */
export const trackEvent = (eventName: EventName, properties?: EventProperties) => {
  posthog.capture(eventName, properties)
  
  // Track event in Sentry
  Sentry.addBreadcrumb({
    category: 'user_action',
    message: eventName,
    level: 'info',
    data: properties
  })
}

/**
 * Identify a user in PostHog and Sentry
 */
export const identifyUser = (userId: string, traits?: Record<string, string | number | boolean | null>) => {
  posthog.identify(userId, traits)
  
  // Identify user in Sentry
  Sentry.setUser({
    id: userId,
    ...traits
  })
}

/**
 * Reset user identification
 */
export const resetUser = () => {
  posthog.reset()
  Sentry.setUser(null)
}

/**
 * Check if a feature flag is enabled
 */
export const isFeatureEnabled = (featureName: string): boolean => {
  return posthog.isFeatureEnabled(featureName)
}

/**
 * Get the current user's distinct ID
 */
export const getDistinctId = (): string => {
  return posthog.get_distinct_id()
}

/**
 * Track an error in Sentry
 */
export const trackError = (error: Error, context?: Record<string, unknown>) => {
  Sentry.captureException(error, {
    extra: context
  })
}

/**
 * Set additional context for error tracking
 */
export const setErrorContext = (context: Record<string, unknown>) => {
  Sentry.setContext('error_context', context)
}

/**
 * Track integration-related events with consistent properties
 */
export const trackIntegrationEvent = (
  eventName: EventName, 
  provider: string, 
  additionalProperties?: EventProperties
) => {
  trackEvent(eventName, {
    provider,
    provider_type: provider.includes('ci') ? 'ci' : 'communication',
    ...additionalProperties
  })
} 